import { FC } from 'react';
import escape from 'lodash.escape';
import Head from 'next/head';
import { addMonths } from 'date-fns';
import { FullBoatSearchDetail, BoatsRelatedApiResponse } from '../../types/api/v4/boats/search';
import { PROD_URL } from '../../constants';
import appendTrailingSlash from '../../utils/appendTrailingSlash';

type BoatFields =
  | 'listing_url'
  | 'headline'
  | 'photo_url'
  | 'make'
  | 'model'
  | 'description'
  | 'year'
  | 'search_price_nested'
  | 'photos'
  | 'total_reviews'
  | 'review_average'
  | 'boat_reviews';
type Boat = Pick<FullBoatSearchDetail, BoatFields>;

/** Minimal keys needed for Trips page `MetaField` `BoatSchema` */
export type RelatedBoatsMeta = Pick<BoatsRelatedApiResponse, 'headline' | 'photo_url' | 'description' | 'listing_url'>;

type BoatSchemaProps = {
  listing: Boat;
  relatedListings: RelatedBoatsMeta[];
};

const BoatSchema: FC<BoatSchemaProps> = ({
  listing,
  relatedListings,
}) => {
  const getListingUrl = (url: string) => appendTrailingSlash(
    PROD_URL + url,
  );
  const listingUrl = getListingUrl(listing.listing_url);
  const totalReviews = listing.total_reviews;
  const boatReviews = listing.boat_reviews;

  // Set priceValidUntil to 6 months from now using date-fns
  const priceValidUntil = addMonths(new Date(), 6).toISOString();

  return (
    <Head>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: escape(listing.headline),
            image: listing.photo_url,
            brand: escape(listing.make),
            manufacturer: escape(listing.make),
            model: escape(listing.model),
            description: escape(listing.description),
            productionDate: escape(listing.year),
            url: listingUrl,
            ...(totalReviews && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: listing.review_average,
                reviewCount: totalReviews,
              },
            }),
            ...(boatReviews && boatReviews.length > 0 && {
              review: boatReviews.map(({
                public_review,
                date_created,
                user,
                rating,
              }) => ({
                '@type': 'Review',
                itemReviewed: 'Boat',
                reviewBody: escape(public_review),
                datePublished: date_created,
                author: {
                  '@type': 'Person',
                  name: escape(user.first_name),
                },
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: rating,
                  bestRating: 5,
                  worstRating: 1,
                },
              })),
            }),
            isRelatedTo: relatedListings.map(({
              headline,
              photo_url,
              description,
              listing_url,
            }) => ({
              '@type': 'Product',
              name: escape(headline),
              image: photo_url,
              description: escape(description),
              url: getListingUrl(listing_url),
            })),
            offers: [{
              '@type': 'Offer',
              availability: 'https://schema.org/OnlineOnly',
              url: listingUrl,
              price: listing.search_price_nested.price,
              priceCurrency: listing.search_price_nested.currency,
              priceSpecification: {
                '@type': 'PriceSpecification',
                price: listing.search_price_nested.price,
                priceCurrency: listing.search_price_nested.currency,
                eligibleQuantity: {
                  '@type': 'QuantitativeValue',
                  minValue: listing.search_price_nested.minimum,
                  unitText: listing.search_price_nested.label,
                },
              },
              priceValidUntil,
            }],
          }),
        }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ImageGallery',
            image: listing.photos.map(({ url }) => url),
          }),
        }}
      />
    </Head>
  );
};

export default BoatSchema;
